/**
 * Pure mapping for the chart Labs results view (PROD-2643).
 *
 * Input: one lab request row from the lab desk list (`GetPatientLabDetails`, which carries the
 * PatientLabRequest id, name, vendor and date) plus that request's Reports read (PROD-2641,
 * Scrips.Patient #997). Output: one render-ready group per lab request, with its ordered tests
 * (PatientLabRequestTest) and the analytes (LabObservation) of the CURRENT report versions.
 *
 * No React here, so the grouping, completeness, flag and trend rules are testable on their own.
 */
import type { PatientDocument } from '@/types/patient-chart-api';
import {
  buildAnalyteVersions,
  reportProvenance,
  type LabAnalyteVersion,
  type LabReportProvenance,
} from './lab-provenance';
import { clinicDateD45, zonedInstant } from '@/shared/lib/clinic-time';
import { asUtcInstant } from '@/shared/lib/utc-instant';
import type { PatientLabOrderDetail } from '@/types/scheduling-api';
import {
  LAB_REQUEST_TEST_STATUS,
  type LabObservation,
  type LabObservationTrendPoint,
  type LabReport,
  type LabRequestReports,
  type LabRequestTestSummary,
} from '@/types/lab-results-api';

// ─── Flags ──────────────────────────────────────────────────────────────────

export interface LabFlagMeta {
  code: 'H' | 'L' | 'HH' | 'LL' | 'A' | 'AA';
  /** Screen-reader and tooltip text. */
  label: string;
  /** HH / LL / AA: a critical value, rendered emphasised. */
  critical: boolean;
}

const FLAGS: Record<LabFlagMeta['code'], LabFlagMeta> = {
  H: { code: 'H', label: 'High', critical: false },
  L: { code: 'L', label: 'Low', critical: false },
  A: { code: 'A', label: 'Abnormal', critical: false },
  HH: { code: 'HH', label: 'Critical high', critical: true },
  LL: { code: 'LL', label: 'Critical low', critical: true },
  AA: { code: 'AA', label: 'Critical abnormal', critical: true },
};

/**
 * N (and anything unknown or empty) is no flag: a normal result shows no chip. An uploaded report's
 * printed flag (interpretationText) goes through here too, so only an exact code becomes a chip:
 * printed text such as "HIGH", "H*" or "*" shows none rather than a guessed one. Never computed.
 */
export function labFlag(interpretation: string | null | undefined): LabFlagMeta | null {
  const code = (interpretation ?? '').trim().toUpperCase();
  return code in FLAGS ? FLAGS[code as LabFlagMeta['code']] : null;
}

/** An exact flag code: one labFlag shows as a chip, or N (normal: no chip, but the note quotes it). */
function exactFlagCode(text: string | null | undefined): string | undefined {
  const code = text?.trim().toUpperCase();
  return code && (code === 'N' || labFlag(code)) ? code : undefined;
}

/**
 * The flag code of one result, upper-cased, as the chart Labs table and the note's lab pieces both
 * read it: the interpretation when set (it wins, and when it is not an exact code there is no flag),
 * else the printed flag of an uploaded report, only when it is an exact code. Never computed.
 */
export function labFlagCode(o: Pick<LabObservation, 'interpretation' | 'interpretationText'>): string | undefined {
  if (o.interpretation?.trim()) return exactFlagCode(o.interpretation);
  return exactFlagCode(o.interpretationText);
}

// ─── Times ──────────────────────────────────────────────────────────────────
//
// THE RESULTS API'S TIMES ARE UTC, SENT WITHOUT A ZONE. Scrips.Patient stores them as UTC
// (`Infrastructure/Implementations/Services/LabResults/LabResultRules.cs` `ToUtc`, ~line 375: "the API
// contract: timestamps are UTC"; applied to IssuedAt ~line 124 and EffectiveAt ~line 168) and reads them
// back with no UTC converter (`Domain/Entities/ScripsPatientsContext.cs` LabReport / LabObservation,
// mapped as-is in `LabResultRules.cs` ~lines 331 and 355), so they are serialised as written — UTC digits
// with no marker. Every surface that shows them (the note's chart pieces, this Labs tab, the trend)
// marks them here and shows them on the clinic's clock (`shared/lib/clinic-time.ts`): one result, one
// date, everywhere.

/** A results-API timestamp as the UTC instant it is (`…Z`); one that carries a zone, or a bare date, kept. */
export { asUtcInstant };

/** An instant (ms) for ordering: a zoned time from its parts, a bare date at its UTC midnight, else -Infinity. */
export function labInstant(iso: string | null | undefined): number {
  const marked = asUtcInstant(iso);
  const zoned = zonedInstant(marked);
  if (zoned !== undefined) return zoned;
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(marked ?? '');
  return day ? Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : -Infinity;
}

/**
 * When the specimen was collected, as a UTC instant — or undefined when the record has none. The
 * results API fills a missing collection time with the report's issue time (`LabResultRules.cs` ~line
 * 168: `EffectiveAt = o.EffectiveAt ?? issuedAt`): the same instant on both is that default, not a
 * recorded collection, and is never shown or ranked as one.
 */
export function labCollectedAt(effectiveAt: string | null | undefined, issuedAt: string | null | undefined): string | undefined {
  const effective = asUtcInstant(effectiveAt);
  if (!effective) return undefined;
  const issued = asUtcInstant(issuedAt);
  return issued && labInstant(effective) === labInstant(issued) ? undefined : effective;
}

/**
 * A lab date as text. A `Day` or `Month` date is the calendar date the lab printed, sent as UTC midnight
 * (contract: `2026-09-30T00:00:00Z`): it keeps its digits and never moves to another clock. A `Month`
 * date reads "Sep 2026", so no day is invented. Anything else is the D-45 date on the clinic's calendar.
 */
export function labDateText(iso: string | null | undefined, precision?: string | null): string | undefined {
  const p = precision?.trim();
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if ((p === 'Day' || p === 'Month') && day) {
    const text = clinicDateD45(`${day[1]}-${day[2]}-${day[3]}`);
    return p === 'Month' ? text?.replace(/^\d+ /, '') : text;
  }
  return clinicDateD45(iso);
}

// ─── Values and ranges ──────────────────────────────────────────────────────

const LAB_COMPARATORS: ReadonlySet<string> = new Set(['<', '<=', '>', '>=']);

/**
 * A bound the lab printed instead of a point value (`<0.5`): one of `<`, `<=`, `>`, `>=` (the lab store's
 * CK_LabObservation_ValueComparator). Null for a plain value, and for any other text, which is never shown.
 */
export function labValueComparator(o: Pick<LabObservation, 'valueComparator'>): string | null {
  const c = o.valueComparator?.trim() ?? '';
  return LAB_COMPARATORS.has(c) ? c : null;
}

/**
 * The value as the lab printed it (`valuePrinted`, Document Intake rows) when there is one. Else numeric,
 * else text, else the coded display (or its code). A numeric value keeps the comparator the lab printed
 * (`<0.5`, contract v0.8): dropping it would turn a bound into a point value.
 */
export function formatLabValue(o: LabObservation): string {
  const printed = o.valuePrinted?.trim();
  if (printed) return printed;
  if (o.valueNumeric !== null && o.valueNumeric !== undefined && Number.isFinite(o.valueNumeric)) {
    return `${labValueComparator(o) ?? ''}${o.valueNumeric}`;
  }
  const text = o.valueText?.trim();
  if (text) return text;
  const coded = o.valueCodedDisplay?.trim() || o.valueCodedCode?.trim();
  return coded || '—';
}

const isNum = (n: number | null | undefined): n is number =>
  n !== null && n !== undefined && Number.isFinite(n);

/**
 * The reference range AS THE LAB PRINTED IT (`refText`) first; only when the lab printed none is it
 * rebuilt from the numeric bounds (refLow–refHigh, or a one-sided bound); else a dash. It reaches the
 * signed note, so the lab's own words win over a reconstruction (design desk, #614).
 */
export function formatLabRange(o: LabObservation): string {
  const printed = o.refText?.trim();
  if (printed) return printed;
  if (isNum(o.refLow) && isNum(o.refHigh)) return `${o.refLow}–${o.refHigh}`;
  if (isNum(o.refLow)) return `≥ ${o.refLow}`;
  if (isNum(o.refHigh)) return `≤ ${o.refHigh}`;
  return '—';
}

// ─── Report markers ─────────────────────────────────────────────────────────

const lower = (s: string | null | undefined) => (s ?? '').trim().toLowerCase();

/** A report or analyte not yet final. */
export function isPreliminaryStatus(status: string | null | undefined): boolean {
  const s = lower(status);
  return s === 'preliminary' || s === 'partial' || s === 'registered';
}

/** A report or analyte replaced by a later version. */
export function isAmendedStatus(status: string | null | undefined): boolean {
  const s = lower(status);
  return s === 'amended' || s === 'corrected';
}

/** Withdrawn results never render as values. */
export function isWithdrawn(status: string | null | undefined): boolean {
  const s = lower(status);
  return s === 'cancelled' || s === 'entered-in-error';
}

// ─── Trend key ──────────────────────────────────────────────────────────────

export interface LabTrendKey {
  loinc?: string;
  scripsComponentCode?: string;
  scripsComponentSystem?: string;
  localCode?: string;
  localSystem?: string;
}

/**
 * LOINC when there is one, else the Scrips component code (+ system), else the local code (+ system);
 * null when none. The same precedence the trend endpoint applies (loinc > Scrips > local).
 */
export function trendKeyFor(o: LabObservation): LabTrendKey | null {
  const loinc = o.loinc?.trim();
  if (loinc) return { loinc };
  const scripsComponentCode = o.scripsComponentCode?.trim();
  if (scripsComponentCode) {
    const scripsComponentSystem = o.scripsComponentSystem?.trim();
    return scripsComponentSystem ? { scripsComponentCode, scripsComponentSystem } : { scripsComponentCode };
  }
  const localCode = o.localCode?.trim();
  if (localCode) {
    const localSystem = o.localSystem?.trim();
    return localSystem ? { localCode, localSystem } : { localCode };
  }
  return null;
}

// ─── Trend data ─────────────────────────────────────────────────────────────

/** A sparkline needs at least this many plotted points. */
export const SPARKLINE_MIN_POINTS = 2;

/** The performing lab of a report: as printed on the document, else the vendor the order went to. */
export function reportLabName(r: Pick<LabReport, 'performingLabName' | 'labVendorName'>): string | null {
  return r.performingLabName?.trim() || r.labVendorName?.trim() || null;
}

/** Report id (its root, stable across versions) to its lab name, over every report loaded. */
export function labsByRootReport(reports: LabReport[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const r of reports) {
    const root = r.rootLabReportId ?? r.id;
    const lab = reportLabName(r);
    if (root && lab) out.set(root, lab);
  }
  return out;
}

/** What a trend point must match to join a row's line. */
export interface LabTrendRow {
  unit: string;
  /** The performing lab of the row's own report; null when unknown. */
  lab: string | null;
  refLow: number | null;
  refHigh: number | null;
  refText: string | null;
  /** The row's range regime; null or absent when none (Scrips.Patient #1133). */
  rangeRegime?: string | null;
}

const normUnit = (u: string | null | undefined) => (u ?? '').trim().toLowerCase();
const normText = (t: string | null | undefined) => (t ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
const normBound = (n: number | null | undefined) => (isNum(n) ? n : null);

/**
 * The plotted series for one analyte: numeric with no comparator, current, not withdrawn, and ONLY points that match the
 * row on ALL of: the unit (`unit ?? unitText`, trimmed, case-insensitive; a point with no unit does
 * not join a row that has one), the performing lab (`performingLabName ?? labVendorName` of the
 * point's report, found by `rootLabReportId` in `labs`; an unknown lab never joins), and the range
 * (the same refLow, refHigh, refText and `rangeRegime`; the regime compares whitespace-collapsed and
 * case-insensitive, and none equals none, as in Scrips.Patient's `LabTrendSeries` key). Method and
 * specimen are not stored anywhere yet, so they are not part of the key. Oldest first, re-sorted by
 * effectiveAt defensively.
 */
export function trendSeries(
  points: LabObservationTrendPoint[] | null | undefined,
  row: LabTrendRow,
  labs: ReadonlyMap<string, string>,
): number[] {
  const wantUnit = normUnit(row.unit);
  const wantLab = normText(row.lab);
  const wantText = normText(row.refText);
  const wantRegime = normText(row.rangeRegime);
  const joins = (p: LabObservationTrendPoint): boolean => {
    // The unit must match exactly: a row with no unit joins only points with no unit either.
    if (normUnit(p.unit ?? p.unitText) !== wantUnit) return false;
    const lab = normText(labs.get(p.rootLabReportId ?? ''));
    if (!wantLab || !lab || lab !== wantLab) return false;
    return (
      normBound(p.refLow) === row.refLow &&
      normBound(p.refHigh) === row.refHigh &&
      normText(p.refText) === wantText &&
      normText(p.rangeRegime) === wantRegime
    );
  };
  return (points ?? [])
    .filter((p) => isNum(p.valueNumeric))
    // A printed bound (`<0.5`) is not a point value, so it has no place on the line.
    .filter((p) => labValueComparator(p) === null)
    .filter((p) => p.isCurrent !== false)
    .filter((p) => !isWithdrawn(p.status) && !isWithdrawn(p.reportStatus))
    .filter(joins)
    // The results API's zone-less UTC times, ordered as the instants they are (one clock everywhere).
    .map((p, i) => ({ p, i, t: labInstant(p.effectiveAt ?? p.issuedAt) }))
    .sort((a, b) => {
      const ta = Number.isFinite(a.t) ? a.t : 0;
      const tb = Number.isFinite(b.t) ? b.t : 0;
      return ta - tb || a.i - b.i;
    })
    .map(({ p }) => p.valueNumeric as number);
}

export function shouldShowSparkline(series: number[]): boolean {
  return series.length >= SPARKLINE_MIN_POINTS;
}

// ─── Grouping ───────────────────────────────────────────────────────────────

export interface LabAnalyteRow {
  id: string;
  display: string;
  value: string;
  unit: string;
  range: string;
  flag: LabFlagMeta | null;
  preliminary: boolean;
  amended: boolean;
  refLow: number | null;
  refHigh: number | null;
  /** The range as the lab printed it, for the trend's range-regime match. */
  refText: string | null;
  /** The range regime the lab printed ("Fasting"); null when none. Part of the trend's range match. */
  rangeRegime?: string | null;
  /** The performing lab of this analyte's report; null when unknown (`reportLabName`). */
  lab: string | null;
  trendKey: LabTrendKey | null;
  /** Its versions, newest first, when the value changed between report versions; else null. */
  history: LabAnalyteVersion[] | null;
  /** When the specimen was collected, as a UTC instant (`labCollectedAt`); null when not recorded. */
  collectedAt?: string | null;
  /** Day | Minute | Month (`effectiveAtPrecision`); null when unknown. */
  collectedAtPrecision?: string | null;
}

export type LabTestState =
  | 'resulted'
  | 'awaiting-collection'
  | 'awaiting-result'
  | 'not-performed'
  | 'cancelled';

export interface LabTestGroup {
  id: string;
  display: string;
  state: LabTestState;
  analytes: LabAnalyteRow[];
}

export interface LabReportMeta {
  id: string;
  issuedAt: string | null;
  labVendorName: string | null;
  patientDocumentId: string | null;
  conclusion: string | null;
  preliminary: boolean;
  amended: boolean;
  extracted: boolean;
  /** Who confirmed the extracted rows and when; the amended time (`lab-provenance.ts`). */
  provenance: LabReportProvenance;
  /** When this report version was written into Scrips (upload / Document Intake), UTC; not the test date. */
  createdDate: string | null;
}

export interface LabResultGroup {
  labRequestId: string;
  name: string;
  vendor: string | null;
  date: string | null;
  resultedCount: number;
  totalCount: number;
  notPerformedCount: number;
  tests: LabTestGroup[];
  /** Analytes of the current reports that are not tied to an ordered test. */
  otherAnalytes: LabAnalyteRow[];
  reports: LabReportMeta[];
  /** A PDF linked on the lab request itself (the pre-discrete "Result received" path). */
  requestDocumentId: string | null;
  /** The lab desk list signs that PDF's URL (SAS); null when there is none. */
  requestDocumentUrl: string | null;
  /** An outside (External) report with no lab request: its own card, no ordered tests. */
  outside?: boolean;
}

/**
 * The header's dates: the order date, and the collection date(s) of the group's current results — on the
 * clinic's clock in D-45 (`shared/lib/clinic-time.ts`), the same date the note's chart pieces show for
 * the same result. Several draws in one request list each date once, oldest first.
 */
export function labGroupDates(group: Pick<LabResultGroup, 'date' | 'tests' | 'otherAnalytes'>): { ordered: string | null; collected: string[] } {
  const rows = [...group.tests.flatMap((t) => t.analytes), ...group.otherAnalytes];
  const dated = rows.filter((r) => !!r.collectedAt).sort((a, b) => labInstant(a.collectedAt) - labInstant(b.collectedAt));
  const collected = [...new Set(dated.map((r) => labDateText(r.collectedAt, r.collectedAtPrecision)).filter((d): d is string => !!d))];
  return { ordered: clinicDateD45(group.date) ?? null, collected };
}

/** Awaiting text for a test with no analyte yet; null when the state says nothing. */
export function awaitingLabel(state: LabTestState): string | null {
  switch (state) {
    case 'awaiting-collection':
      return 'Awaiting collection';
    case 'awaiting-result':
      return 'Collected, awaiting result';
    case 'not-performed':
      return 'Not performed';
    default:
      return null;
  }
}

function testState(test: LabRequestTestSummary, hasAnalytes: boolean): LabTestState {
  switch ((test.status ?? '').trim()) {
    case LAB_REQUEST_TEST_STATUS.Cancelled:
      return 'cancelled';
    case LAB_REQUEST_TEST_STATUS.NotPerformed:
      return 'not-performed';
    case LAB_REQUEST_TEST_STATUS.Resulted:
      return 'resulted';
    case LAB_REQUEST_TEST_STATUS.Ordered:
      return hasAnalytes ? 'resulted' : 'awaiting-collection';
    default:
      // Collected / InLab / anything the lab desk invents later: a specimen is out.
      return hasAnalytes ? 'resulted' : 'awaiting-result';
  }
}

function toAnalyte(o: LabObservation, i: number, r: LabReport, all: LabReport[]): LabAnalyteRow {
  return {
    id: o.id ?? `obs-${i}`,
    display: o.display?.trim() || o.loinc || o.localCode || '—',
    value: formatLabValue(o),
    unit: o.unit?.trim() || o.unitText?.trim() || '',
    range: formatLabRange(o),
    flag: labFlag(labFlagCode(o)),
    preliminary: isPreliminaryStatus(o.status),
    amended: isAmendedStatus(o.status),
    refLow: isNum(o.refLow) ? o.refLow : null,
    refHigh: isNum(o.refHigh) ? o.refHigh : null,
    refText: o.refText?.trim() || null,
    rangeRegime: o.rangeRegime?.trim() || null,
    lab: reportLabName(r),
    trendKey: trendKeyFor(o),
    history: buildAnalyteVersions(o, r, all, formatLabValue),
    collectedAt: labCollectedAt(o.effectiveAt, r.issuedAt) ?? null,
    collectedAtPrecision: o.effectiveAtPrecision ?? null,
  };
}

function currentReports(reports: LabReport[] | null | undefined): LabReport[] {
  return (reports ?? []).filter((r) => r.isCurrent !== false && !isWithdrawn(r.status));
}

function toReportMeta(r: LabReport, i: number): LabReportMeta {
  const amended = isAmendedStatus(r.status) || (r.version ?? 1) > 1;
  return {
    id: r.id ?? `report-${i}`,
    issuedAt: r.issuedAt ?? null,
    labVendorName: r.labVendorName ?? null,
    patientDocumentId: r.patientDocumentId ?? null,
    conclusion: r.conclusion?.trim() || null,
    preliminary: isPreliminaryStatus(r.status),
    amended,
    extracted: lower(r.source) === 'extracted',
    provenance: reportProvenance(r, amended),
    createdDate: r.createdDate ?? null,
  };
}

/** One group per lab request: the request row, its tests, and its current analytes. */
export function buildLabResultGroup(
  request: PatientLabOrderDetail,
  reads: LabRequestReports,
): LabResultGroup {
  const reports = currentReports(reads.reports);
  const pairs = reports.flatMap((r) =>
    (r.observations ?? []).filter((o) => o.isCurrent !== false && !isWithdrawn(o.status)).map((o) => ({ o, r })),
  );
  const observations = pairs.map(({ o }) => o);
  // Every version the read returned (includeHistory=true): the amended history is matched across them.
  const analytes = pairs.map(({ o, r }, i) => toAnalyte(o, i, r, reads.reports ?? []));

  const byTest = new Map<string, LabAnalyteRow[]>();
  const other: LabAnalyteRow[] = [];
  const testIds = new Set((reads.tests ?? []).map((t) => t.id).filter(Boolean) as string[]);
  observations.forEach((o, i) => {
    const key = o.patientLabRequestTestId ?? '';
    if (key && testIds.has(key)) {
      byTest.set(key, [...(byTest.get(key) ?? []), analytes[i]]);
    } else {
      other.push(analytes[i]);
    }
  });

  const tests: LabTestGroup[] = (reads.tests ?? [])
    .filter((t) => t.isOmitted !== true)
    .map((t, i) => {
      const own = (t.id && byTest.get(t.id)) || [];
      return {
        id: t.id ?? `test-${i}`,
        display: t.testDisplay?.trim() || t.testCode?.trim() || '—',
        state: testState(t, own.length > 0),
        analytes: own,
      };
    });

  const metas = reports.map(toReportMeta);
  return {
    labRequestId: request.id ?? reads.patientLabRequestId ?? '',
    name: request.orderName?.trim() || 'Lab order',
    vendor: request.labVendorName?.trim() || metas.find((m) => m.labVendorName)?.labVendorName || null,
    date: request.orderDate ?? null,
    resultedCount: reads.resultedCount ?? 0,
    totalCount: reads.totalCount ?? 0,
    notPerformedCount: reads.notPerformedCount ?? 0,
    tests,
    otherAnalytes: other,
    reports: metas,
    requestDocumentId: request.patientDocumentId ?? null,
    requestDocumentUrl: request.documentUrl ?? null,
  };
}

/** The current outside (External) reports: no lab request, so each is its own card. */
export function currentExternalReports(reports: LabReport[] | null | undefined): LabReport[] {
  return currentReports(reports);
}

/**
 * One group per outside report (PROD-3204, no lab request): every analyte in one table, no ordered
 * tests. `history` is every version the read returned (`includeHistory=true`).
 */
export function buildExternalLabResultGroup(report: LabReport, history: LabReport[] = [report]): LabResultGroup {
  const meta = toReportMeta(report, 0);
  const live = (report.observations ?? []).filter((o) => o.isCurrent !== false && !isWithdrawn(o.status));
  return {
    labRequestId: report.rootLabReportId ?? report.id ?? 'outside-report',
    name: live[0]?.display?.trim() || 'Outside lab report',
    vendor: reportLabName(report) ?? 'Lab not printed',
    date: null,
    resultedCount: 0,
    totalCount: 0,
    notPerformedCount: 0,
    tests: [],
    otherAnalytes: live.map((o, i) => toAnalyte(o, i, report, history)),
    reports: [meta],
    // No request document: the report's own file is found through the document lookup (View report).
    requestDocumentId: null,
    requestDocumentUrl: null,
    outside: true,
  };
}

/**
 * The card's meta line. An ordered request: "<lab> · Ordered <date> · Collected <dates>". An outside
 * report: "<lab or Lab not printed> · Outside report · no order · Collected <dates>".
 */
export function labGroupMetaLine(group: LabResultGroup): string {
  const dates = labGroupDates(group);
  const collected = dates.collected.length ? `Collected ${dates.collected.join(', ')}` : null;
  const parts = group.outside
    ? [group.vendor ?? 'Lab not printed', 'Outside report · no order', collected]
    : [group.vendor, dates.ordered ? `Ordered ${dates.ordered}` : null, collected];
  return parts.filter(Boolean).join(' · ') || '—';
}

/**
 * The lab desk list joins PatientDocumentFile, so a request whose PDF has two files comes back
 * twice. Keep the first row per request id.
 */
export function uniqueLabRequests(rows: PatientLabOrderDetail[] | null | undefined): PatientLabOrderDetail[] {
  const seen = new Set<string>();
  const out: PatientLabOrderDetail[] = [];
  for (const row of rows ?? []) {
    if (!row.id || seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
  }
  return out;
}

/** The first live file URL of a patient document, as the Documents tab opens it. */
export function fileUrlFor(
  documents: PatientDocument[] | null | undefined,
  patientDocumentId: string,
): string | null {
  const doc = (documents ?? []).find((d) => d.id === patientDocumentId);
  const file = doc?.patientDocumentFiles?.find((f) => f?.isDeleted !== true && f?.url);
  return file?.url ?? null;
}
